import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui";
import { Calendar, Star, Heart, Users } from "lucide-react";
import { useUnsplashImages } from "@/hooks/useUnsplashImages";
import { Link } from 'react-router-dom';

const EquestrianTraining = () => {
  const [heroImage, setHeroImage] = useState("");
  const { data: equestrianImages } = useUnsplashImages("horse riding lessons children", 1);
  
  useEffect(() => {
    if (equestrianImages && equestrianImages.length > 0) {
      setHeroImage(equestrianImages[0].urls.regular);
    }
  }, [equestrianImages]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Equestrian Training Management - Hivetivity</title>
        <meta name="description" content="Streamline your equestrian training program with Hivetivity. Manage lessons, track progress, and communicate with riders and parents efficiently." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-amber-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Elevate Your Equestrian Training Program
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Focus on developing skilled riders while we handle the administrative tasks with our comprehensive management platform.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                {heroImage ? (
                  <OptimizedImage 
                    src={heroImage}
                    alt="Young riders during training"
                    width={600}
                    height={400}
                    className="rounded-2xl shadow-xl"
                    objectFit="cover"
                  />
                ) : (
                  <div className="rounded-2xl bg-gray-200 w-full h-[400px] animate-pulse flex items-center justify-center">
                    <p className="text-gray-500">Loading image...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your Equestrian Program Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors">
                <Calendar className="w-12 h-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Lesson Management</h3>
                <p className="text-gray-600">Schedule and coordinate riding lessons efficiently.</p>
              </div>
              <div className="p-6 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors">
                <Star className="w-12 h-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Progress Tracking</h3>
                <p className="text-gray-600">Monitor rider development and achievements.</p>
              </div>
              <div className="p-6 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors">
                <Heart className="w-12 h-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Horse Care</h3>
                <p className="text-gray-600">Track horse assignments and maintenance schedules.</p>
              </div>
              <div className="p-6 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors">
                <Users className="w-12 h-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Parent Portal</h3>
                <p className="text-gray-600">Keep families updated on progress and events.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Trusted by Equestrian Centers
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has streamlined our entire operation. From lesson scheduling to progress tracking, everything is now perfectly organized."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - Emily Richardson, Riding Academy Director
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Transform Your Equestrian Program?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join successful equestrian centers using Hivetivity to create exceptional riding programs.
            </p>
            <Link to="/business-trial">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EquestrianTraining;
